<template>
  <div class="stats-summary">
    <div class="stats-tiles">
      <div class="stat-tile admin-card">
        <span class="admin-label">Cards in correct position</span>
        <span class="stat-tile__value">{{ totals.percentCorrect }}<small>%</small></span>
        <span class="stat-tile__meta">
          {{ cardsCorrect }} of {{ cardsTotal }} placements
        </span>
      </div>

      <div class="stat-tile admin-card">
        <span class="admin-label">Mistakes per game</span>
        <span class="stat-tile__value">{{ totals.avgMistakesPerGame }}</span>
        <span class="stat-tile__meta">{{ totalMistakes }} in total</span>
      </div>

      <div class="stat-tile admin-card">
        <span class="admin-label">Avg time per deal</span>
        <span class="stat-tile__value">{{ formatDuration(totals.avgDealMs) }}</span>
        <span class="stat-tile__meta">Deal to "I'm Done"</span>
      </div>

      <div class="stat-tile admin-card">
        <span class="admin-label">Avg cards dealt per game</span>
        <span class="stat-tile__value">{{ totals.avgCardsDealtPerGame }}</span>
        <span class="stat-tile__meta">{{ totals.gamesPlayed }} games played</span>
      </div>

      <div class="stat-tile admin-card">
        <span class="admin-label">Longest streak</span>
        <span class="stat-tile__value">{{ totals.longestStreak }}</span>
        <span class="stat-tile__meta">Cards correct in a row, across deals</span>
      </div>

      <div class="stat-tile admin-card">
        <span class="admin-label">Total time played</span>
        <span class="stat-tile__value">{{ formatDuration(totals.totalPlayMs) }}</span>
        <span class="stat-tile__meta">Active gameplay only</span>
      </div>
    </div>

    <div class="admin-table-wrap">
      <div class="admin-card-head">
        <h3>Game history</h3>
        <span class="admin-card-count">{{ sessions.length }} games</span>
      </div>

      <div class="admin-table-wrap--scroll">
        <table class="admin-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Played</th>
              <th>Department</th>
              <th class="numeric">Correct</th>
              <th class="numeric">Mistakes</th>
              <th class="numeric">Cards dealt</th>
              <th class="numeric">Avg / deal</th>
              <th class="numeric">Streak</th>
              <th class="numeric">Time</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td>{{ row.index }}</td>
              <td>{{ row.playedAt }}</td>
              <td>
                {{ row.department }}
                <span v-if="row.is_demo" class="admin-badge admin-badge--neutral">Demo</span>
              </td>
              <td class="numeric">
                <span class="accuracy" :class="row.accuracyBand">{{ row.percentCorrect }}%</span>
              </td>
              <td class="numeric">{{ row.mistakes }}</td>
              <td class="numeric">{{ row.cards_dealt }}</td>
              <td class="numeric">{{ formatDuration(row.avg_deal_ms) }}</td>
              <td class="numeric">{{ row.longest_streak }}</td>
              <td class="numeric">{{ formatDuration(row.total_play_ms) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { aggregateHistory, formatDuration } from '@/utils/gameStats';

const props = defineProps({
  sessions: {
    type: Array,
    default: () => [],
  },
  departments: {
    type: Array,
    default: () => [],
  },
});

const totals = computed(() => aggregateHistory(props.sessions));

const cardsTotal = computed(() =>
  props.sessions.reduce((sum, s) => sum + (s.cards_total || 0), 0)
);
const cardsCorrect = computed(() =>
  props.sessions.reduce((sum, s) => sum + (s.cards_correct || 0), 0)
);
const totalMistakes = computed(() =>
  props.sessions.reduce((sum, s) => sum + (s.mistakes || 0), 0)
);

const departmentName = (id) => {
  const match = props.departments.find((d) => d.id === id);
  return match ? match.name : 'Unknown';
};

// Newest first reads better in a table, even though charts run oldest first.
const rows = computed(() =>
  props.sessions
    .map((session, i) => {
      const percentCorrect = session.cards_total
        ? Math.round((session.cards_correct / session.cards_total) * 1000) / 10
        : 0;

      return {
        ...session,
        index: i + 1,
        percentCorrect,
        accuracyBand: percentCorrect >= 80 ? 'is-high' : percentCorrect >= 50 ? 'is-mid' : 'is-low',
        department: departmentName(session.department_id),
        playedAt: new Date(session.started_at).toLocaleString(undefined, {
          day: 'numeric',
          month: 'short',
          hour: '2-digit',
          minute: '2-digit',
        }),
      };
    })
    .reverse()
);
</script>
